import { Maybe } from '../../common/data.js';
import { Component, h } from 'preact';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import { PublicStore } from '../../common/data/stores.js';
import { queue, within } from '../../common/util.js';
import { ApiError } from '../../common/errors.js';
import { showErrorAlert } from '../components/popup.js';

dayjs.extend(quarterOfYear);

export function nl2p(s: string) {
	return s.split('\n').filter(ss => !!ss.trim()).map(s => h('p', {}, [s.trim()]));
}

const salt = 'chloride';
export async function digestPassword(text: string) {
	let buf = (new TextEncoder()).encode(text + salt).buffer as ArrayBuffer;
	for (let i = 0; i < 100; i++) buf = await window.crypto.subtle.digest('SHA-256', buf);
	return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export const getStoreUrl = (store: PublicStore, rest = '') => `/store/${store.url ?? store.id}${rest}`;

export const getEventValue = (e: Event) => (e.target as HTMLInputElement).value;
export const getEventChecked = (e: Event) => (e.target as HTMLInputElement).checked;


export type ClassNameArray = Array<Maybe<string | boolean | ClassNameArray>>;
export const classNames = (...names: ClassNameArray) => names
	.flat(10)
	.filter(it => !!it)
	.map(cn => String(cn)[0] == '-' ? String(names[0]) + cn : cn)
	.join(' ');
	
export const blockClass = (prefix: string) => (...names: ClassNameArray) => !names.length ? prefix : names
	.flat(10)
	.filter(it => !!it)
	.map(cn => String(cn)[0] == '_' ? prefix + cn : cn)
	.map((cn, _i, arr) => String(cn)[0] == '-' ? String(arr[0]) + cn : cn)
	.join(' ');

export const textToParagraphs = (text: string) => text
	.split(/\n\n+/g)
	.map(p => <p>{p.split(/\n+/g).map((s, i) => <>
		{s.split(/(\bhttps:\/\/\S+)/g).map(
			c => c.match(/^https:\/\//) ? <a href={c.replace(/[.!?)]$/, '')}>{c}</a> : c
		)}
		{i > 0 ? <br /> : ' '}
	</>)}</p>);

export interface SubmitOptions<T extends Component> {
	key?: keyof T['state'];
	doneKey?: keyof T['state'];
	errorKey?: keyof T['state'];
	onError?: (this: T, e: Error) => void | Promise<void>;
}

export function withSubmitQueue<T extends Component<any, any>>({ key = 'submitting' as keyof T['state'], doneKey, errorKey, onError }: SubmitOptions<T> = {}) {
	return function submitQueue<A extends any[], R>(fn: (...args: A) => Promise<R>, context: ClassMethodDecoratorContext<T>) {
		context.addInitializer(function () {
			let done = false;
			// @ts-ignore it's the right key and signature!
			this[context.name] = queue(async (...args: A): Promise<R | void> => {
				if (args[0] instanceof Event) {
					args[0].preventDefault();
					args[0].stopPropagation();
				}
				if (done) return;
				this.setState({
					[key]: true,
					...errorKey ? { [errorKey]: null } : {},
				});
				const ret = await fn.call(this, ...args)
					.catch(e => {
						if (onError) return onError.call(this, e);
						if (!(e instanceof ApiError)) throw e;
						if (errorKey) return this.setState({ [errorKey]: e });
						return showErrorAlert(e);
					})
					.finally(() => this.setState({ [key]: false }));
				if (ret === true) {
					done = true;
					if (doneKey) this.setState({ [doneKey]: true });
				}
				return ret;
			});
		});
	};
}

export const submitQueue = withSubmitQueue<Component<any, any>>();

// TODO do something properly reusable here
const ttWidth = 300;
export const realignTooltip = (e: Event) => {
	const ctr = e.currentTarget as Maybe<HTMLElement>;
	const tt = ctr?.querySelector('.tooltip-text') as Maybe<HTMLElement>;
	if (!ctr || !tt) return;
	const scroll = window.scrollX;
	const ww = window.innerWidth;
	const box = ctr.getBoundingClientRect();
	const left = within(scroll + 4, box.left, scroll + ww - 4 - ttWidth) - box.left;
	tt.style.left = `${Math.round(left)}px`;
};

